import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Car, LogOut, Home, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { STATUS_LABELS, STATUS_COLORS } from "@/lib/constants";

export function DashboardShell({ title, nav, active, onNav, children, accent = "navy" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const SideContent = () => (
    <>
      <Link to="/" className="flex items-center gap-2 px-2 py-1">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white"><Car size={18} /></span>
        <span className="font-heading text-base font-bold text-white">SmartFares<span className="text-brand">4U</span></span>
      </Link>
      <p className="mt-1 px-2 text-xs uppercase tracking-wider text-slate-500">{title}</p>
      <nav className="mt-4 flex flex-1 flex-col gap-1 overflow-auto no-scrollbar">
        {nav.map((n) => (
          <button key={n.key} onClick={() => { onNav(n.key); setOpen(false); }} data-testid={`nav-${n.key}`}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${active === n.key ? "bg-brand text-white" : "text-slate-300 hover:bg-white/5"}`}>
            <n.icon size={17} /> {n.label}
          </button>
        ))}
      </nav>
      <div className="mt-4 space-y-1 border-t border-white/10 pt-3">
        <button onClick={() => navigate("/")} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"><Home size={16} /> Back to site</button>
        <button onClick={async () => { await logout(); navigate("/"); }} data-testid="dash-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"><LogOut size={16} /> Log out</button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col bg-navy p-4 lg:flex"><SideContent /></aside>
      {open && <div className="fixed inset-0 z-50 lg:hidden"><div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} /><aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-navy p-4"><SideContent /></aside></div>}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="dash-menu"><Menu size={18} /></Button>
            <h1 className="font-heading text-lg font-bold text-navy">{nav.find((n) => n.key === active)?.label || title}</h1>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="hidden text-slate-500 sm:inline">{user?.name}</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">{user?.name?.[0]}</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, sub, color = "brand" }) {
  const colors = { brand: "bg-brand-light text-brand", emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", navy: "bg-slate-100 text-navy" };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${colors[color]}`}><Icon size={18} /></span>
      </div>
      <p className="mt-3 font-heading text-2xl font-bold text-navy">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
      {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export function StatusBadge({ status }) {
  return <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>{STATUS_LABELS[status] || status}</span>;
}
